import { useTranslation } from '@/utils/i18n/useTranslation';
import { useEffect, useRef, useContext } from 'react';

import Svg from '@/components/Svg/Svg';
import { UserContext } from '@/context/UserContext';

interface InfoBoxProps {
  content?: { tooltipText?: string }[];
  modifiers?: string[];
  match?: boolean;
}

// eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- value is present by contract here but the GraphQL type is nullable; re-type from codegen to remove
const createMarkup = (copy?: string) => ({ __html: copy! });

// Stable reference so addEventListener/removeEventListener match (an inline
// arrow never removes — the listener argument must be a function reference).
const preventClickDefault = (event: Event) => {
  event.preventDefault();
};

const getHeadingLength = (
  el: Element | null,
  className: string,
): number | null => {
  while (el?.parentNode) {
    el = el.parentNode as Element;
    if (el.classList.contains(className)) {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- value is present by contract here but the GraphQL type is nullable; re-type from codegen to remove
      return (el.parentNode as Element).firstElementChild!.clientWidth;
    }
  }
  return null;
};

const InfoBox = (props: InfoBoxProps) => {
  const { content, modifiers, match } = props;

  const modifierClasses = modifiers ? ' ' + modifiers.join(' ') : '';
  const userContext = useContext(UserContext);

  /* eslint-disable @typescript-eslint/no-unnecessary-condition -- optimistic GraphQL type — this guard defends a null the generated type doesn't model; re-type from codegen to remove */
  const { t } = useTranslation(
    userContext?.userSession?.sportType?.slug ??
      process.env.NEXT_PUBLIC_RINUS_DEFAULT_NAMESPACE ??
      process.env.NEXT_PUBLIC_RINUS_DEFAULT_SPORT,
  );
  /* eslint-enable @typescript-eslint/no-unnecessary-condition */

  const pointerRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLAnchorElement>(null);

  useEffect(() => {
    const tooltip = tooltipRef.current;
    tooltip?.addEventListener('click', preventClickDefault);
    // Needs a timeout to wait for the header to be rendered
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- optimistic GraphQL type — this guard defends a null the generated type doesn't model; re-type from codegen to remove
    if (!pointerRef?.current) return;
    setTimeout(() => {
      const headingLength = getHeadingLength(
        pointerRef.current,
        'InfoBox-wrapper',
      );
      /* eslint-disable @typescript-eslint/no-non-null-assertion, @typescript-eslint/no-unnecessary-condition -- value is present by contract here but the GraphQL type is nullable; re-type from codegen to remove */
      if (pointerRef?.current)
        pointerRef.current.style.left = `${String(headingLength! + 4)}px`;
      /* eslint-enable @typescript-eslint/no-non-null-assertion, @typescript-eslint/no-unnecessary-condition */
    }, 1000);

    return () => tooltip?.removeEventListener('click', preventClickDefault);
  }, []);

  return (
    <>
      <a ref={tooltipRef} href="#" className="InfoBox--focus">
        {match && <span> {t('INFOBOX__LABEL')} </span>}
        <Svg id="icon-explainer" modifiers={['InfoBox--infoBoxIcon']} />
      </a>
      <div
        className={`InfoBox-wrapper has-infobox${modifierClasses}`}
        tabIndex={'0' as unknown as number}
      >
        <div className="InfoBox">
          <div className="InfoBox-inner">
            <span
              className="InfoBox-content"
              dangerouslySetInnerHTML={createMarkup(content?.[0]?.tooltipText)}
            ></span>
            <div ref={pointerRef} className="InfoBox-pointer"></div>
          </div>
        </div>
      </div>
    </>
  );
};

export default InfoBox;
